/**
 * Fuzzy match of a spoken transcript against ask_user question options.
 * Returns {selectedValue} when a match is found (exact label/value match,
 * ordinal "option one" → index, or substring containment), otherwise
 * {freeText} with the raw transcript.
 */

const ORDINALS = {
  first: 0, second: 1, third: 2, fourth: 3, fifth: 4,
  sixth: 5, seventh: 6, eighth: 7, ninth: 8, tenth: 9,
  one: 0, two: 1, three: 2, four: 3, five: 4,
  six: 5, seven: 6, eight: 7, nine: 8, ten: 9,
  '1': 0, '2': 1, '3': 2, '4': 3, '5': 4,
  '6': 5, '7': 6, '8': 7, '9': 8, '10': 9,
};

const ORDINAL_PREFIX = /^(?:option|number|pick) /i;

function normalize(str) {
  let text = str.trim().toLowerCase();
  while (text.length && '.!?'.includes(text[text.length - 1])) text = text.slice(0, -1);
  return text;
}

/**
 * Match a spoken transcript against ask_user question options.
 * @param {string} transcript — raw STT output
 * @param {{ question: string, options: Array<{value: string, label: string}>, allowFreeText?: boolean }} question
 * @returns {{ selectedValue: string } | { freeText: string }}
 */
function matchAskOption(transcript, question) {
  if (!transcript || !transcript.trim()) return { freeText: '' };
  if (!question || !question.options || !question.options.length) return { freeText: transcript.trim() };

  const text = normalize(transcript);
  const opts = question.options;

  // 1. Ordinal match: "option 2", "number three", "first", etc.
  const stripped = text.replace(ORDINAL_PREFIX, '');
  if (ORDINALS[stripped] != null && ORDINALS[stripped] < opts.length) {
    return { selectedValue: opts[ORDINALS[stripped]].value };
  }

  // 2. Exact value match (case-insensitive)
  for (const opt of opts) {
    if (normalize(opt.value) === text) return { selectedValue: opt.value };
  }

  // 3. Exact label match (case-insensitive)
  for (const opt of opts) {
    if (normalize(opt.label) === text) return { selectedValue: opt.value };
  }

  // 4. Label substring containment — transcript is contained in a label, or vice versa
  for (const opt of opts) {
    const normLabel = normalize(opt.label);
    if (normLabel.includes(text) || text.includes(normLabel)) return { selectedValue: opt.value };
  }

  // 5. Label word overlap — at least 60% of label words appear in the transcript
  for (const opt of opts) {
    const labelWords = normalize(opt.label).split(/\s+/);
    const transcriptWords = new Set(text.split(/\s+/));
    const overlap = labelWords.filter((w) => transcriptWords.has(w)).length;
    if (labelWords.length > 0 && overlap / labelWords.length >= 0.6) return { selectedValue: opt.value };
  }

  // No match — return as free text
  return { freeText: transcript.trim() };
}

export { matchAskOption, normalize, ORDINALS };
